import React, { useState, useEffect, useCallback } from 'react';
import axios from "../utils/axiosInstance";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { getApiBase } from "../utils/useApiBase";
import DateOfBirthInput from "./DateOfBirthInput";

const UmuntuUmwirondoro = () => {
  const [formData, setFormData] = useState({
    amazina_yombi: '',
    igitsina: 'Gabo',
    itariki_amavuko: '',
    nimero_nida: '',
    nimero_telefone: '',
    nimero_wihutirwa: '',
  });

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeField, setActiveField] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // ─── NEW: Field-level validation errors ──────────────────────────
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // ─── Validation Helpers ──────────────────────────────────────────
  const validateNida = useCallback((value) => {
    if (!value) return null; // not required, so empty is ok
    const cleaned = value.replace(/\s/g, '');
    if (cleaned.length < 3) return null; // still typing
    if (!cleaned.startsWith('119') && !cleaned.startsWith('120')) {
      return 'Nimero ya NIDA igomba gutangira na 119 cyangwa 120';
    }
    if (cleaned.length > 0 && cleaned.length !== 16) {
      return `Nimero ya NIDA igomba kuba imibare 16 (ubu: ${cleaned.length})`;
    }
    if (!/^\d+$/.test(cleaned)) {
      return 'Nimero ya NIDA igomba kuba imibare gusa';
    }
    return null;
  }, []);

  const validatePhone = useCallback((value) => {
    if (!value) return null;
    const cleaned = value.replace(/\s/g, '');
    if (cleaned.length < 3) return null;
    if (!/^07[2-9]\d{7}$/.test(cleaned) && cleaned.length >= 10) {
      return 'Nimero ya telefone igomba gutangira na 07X (urugero: 0781234567)';
    }
    if (cleaned.length > 0 && cleaned.length !== 10 && cleaned.length >= 4) {
      return `Nimero ya telefone igomba kuba imibare 10 (ubu: ${cleaned.length})`;
    }
    return null;
  }, []);

  const validateDate = useCallback((value) => {
    if (!value) return null;
    const date = new Date(value);
    const today = new Date();
    if (date > today) {
      return "Itariki y'amavuko ntishobora kuba mu gihe kizaza";
    }
    const age = today.getFullYear() - date.getFullYear();
    if (age > 150) {
      return "Itariki y'amavuko ntiyemewe (imyaka irenze 150)";
    }
    return null;
  }, []);

  const validateName = useCallback((value) => {
    if (!value) return 'Amazina yombi arakenewe';
    if (value.trim().length < 3) {
      return 'Amazina agomba kuba nibura inyuguti 3';
    }
    if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(value)) {
      return 'Amazina agomba kuba inyuguti gusa';
    }
    const parts = value.trim().split(/\s+/);
    if (parts.length < 2) {
      return 'Andika amazina yombi (izina n\'irindi)';
    }
    return null;
  }, []);

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'amazina_yombi': return validateName(value);
      case 'nimero_nida': return validateNida(value);
      case 'nimero_telefone': return validatePhone(value);
      case 'nimero_wihutirwa': return validatePhone(value);
      case 'itariki_amavuko': return validateDate(value);
      default: return null;
    }
  }, [validateName, validateNida, validatePhone, validateDate]);

  const validateAllFields = useCallback(() => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const err = validateField(key, formData[key]);
      if (err) errors[key] = err;
    });
    return errors;
  }, [formData, validateField]);

  const isFormValid = useCallback(() => {
    const errors = validateAllFields();
    return Object.keys(errors).length === 0 && formData.amazina_yombi.trim().length > 0;
  }, [validateAllFields, formData.amazina_yombi]);

  // ─── NIDA Auto-format (add spaces) ──────────────────────────────
  const formatNida = useCallback((value) => {
    const cleaned = value.replace(/\s/g, '').replace(/\D/g, '');
    const limited = cleaned.slice(0, 16);
    // Format: X XXXX X XXXXXXX X XX
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
      if (i === 1 || i === 5 || i === 6 || i === 13 || i === 14) {
        formatted += ' ';
      }
      formatted += limited[i];
    }
    return formatted;
  }, []);

  // ─── Phone Auto-format ──────────────────────────────────────────
  const formatPhone = useCallback((value) => {
    const cleaned = value.replace(/\s/g, '').replace(/\D/g, '');
    const limited = cleaned.slice(0, 10);
    // Format: 07XX XXX XXX
    if (limited.length <= 4) return limited;
    if (limited.length <= 7) return `${limited.slice(0, 4)} ${limited.slice(4)}`;
    return `${limited.slice(0, 4)} ${limited.slice(4, 7)} ${limited.slice(7)}`;
  }, []);

  // ─── Responsive Breakpoint Detection ─────────────────────────────
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 576;
  const isTablet = windowWidth >= 576 && windowWidth < 992;
  const isDesktop = windowWidth >= 992;

  useEffect(() => {
    if (isMobile) setViewMode('cards');
  }, [isMobile]);

  useEffect(() => {
    fetchRecords();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${getApiBase()}/umuntu_umwirondoro`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data);
    } catch (err) {
      setError(typeof err.response?.data?.error === 'string' ? err.response.data.error : 'Byanze gufata amakuru');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Auto-format NIDA
    if (name === 'nimero_nida') {
      processedValue = formatNida(value);
    }

    // Auto-format phone numbers
    if (name === 'nimero_telefone' || name === 'nimero_wihutirwa') {
      processedValue = formatPhone(value);
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));

    // Real-time validation for touched fields
    if (touchedFields[name]) {
      const err = validateField(name, processedValue);
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (err) next[name] = err;
        else delete next[name];
        return next;
      });
    }
  }, [formatNida, formatPhone, touchedFields, validateField]);

  // ─── Handle field blur (mark as touched & validate) ──────────────
  const handleBlur = useCallback((name) => {
    setActiveField(null);
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, formData[name]);
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  }, [formData, validateField]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((k) => (allTouched[k] = true));
    setTouchedFields(allTouched);

    // Validate all fields
    const errors = validateAllFields();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError('Kosora amakosa yose mbere yo kohereza');
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      return;
    }

    setSubmitting(true);

    // Clean NIDA and phone before sending
    const cleanedData = {
      ...formData,
      nimero_nida: formData.nimero_nida.replace(/\s/g, ''),
      nimero_telefone: formData.nimero_telefone.replace(/\s/g, ''),
      nimero_wihutirwa: formData.nimero_wihutirwa.replace(/\s/g, ''),
    };

    try {
      const token = localStorage.getItem('token');
      if (editingId) {
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL || ''}/umuntu_umwirondoro/${editingId}`,
          cleanedData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(res.data.message || 'Byagenze neza! Amakuru yavuguruwe.');
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL || ''}/umuntu_umwirondoro`,
          cleanedData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess(res.data.message || 'Byagenze neza! Umuntu yanditswe.');
      }
      fetchRecords();
      resetForm();
      setEditingId(null);
      setShowForm(false);
      if (navigator.vibrate) navigator.vibrate(50);
    } catch (err) {
      setError(typeof err.response?.data?.error === 'string' ? err.response.data.error : 'Byanze kwandika');
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (person) => {
    const nidaFormatted = person.nimero_nida ? formatNida(person.nimero_nida) : '';
    const phoneFormatted = person.nimero_telefone ? formatPhone(person.nimero_telefone) : '';
    const emergFormatted = person.nimero_wihutirwa ? formatPhone(person.nimero_wihutirwa) : '';
    const dateVal = person.itariki_amavuko
      ? new Date(person.itariki_amavuko).toISOString().split('T')[0]
      : '';
    setFormData({
      amazina_yombi: person.amazina_yombi || '',
      igitsina: person.igitsina || 'Gabo',
      itariki_amavuko: dateVal,
      nimero_nida: nidaFormatted,
      nimero_telefone: phoneFormatted,
      nimero_wihutirwa: emergFormatted,
    });
    setEditingId(person.id);
    setFieldErrors({});
    setTouchedFields({});
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL || ''}/umuntu_umwirondoro/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Umuntu yasibwe neza!');
      fetchRecords();
    } catch (err) {
      setError(typeof err.response?.data?.error === 'string' ? err.response.data.error : 'Byanze gusiba');
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      amazina_yombi: '',
      igitsina: 'Gabo',
      itariki_amavuko: '',
      nimero_nida: '',
      nimero_telefone: '',
      nimero_wihutirwa: '',
    });
    setFieldErrors({});
    setTouchedFields({});
    setEditingId(null);
  };

  const filteredRecords = records.filter((person) =>
    person.amazina_yombi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.nimero_nida?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    person.nimero_telefone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0]?.toUpperCase() || '?';
  };

  // ─── Age Calculation Helper ─────────────────────────────────
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // ─── NEW: Format date for display ──────────────────────────────
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = [
      'Mutarama', 'Gashyantare', 'Werurwe', 'Mata', 'Gicurasi', 'Kamena',
      'Nyakanga', 'Kanama', 'Nzeri', 'Ukwakira', 'Ugushyingo', 'Ukuboza'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // ─── NEW: Get NIDA validity indicator ──────────────────────────
  const getNidaStatus = (nida) => {
    if (!nida) return null;
    const cleaned = nida.replace(/\s/g, '');
    if (cleaned.length < 3) return { status: 'typing', color: '#64748b' };
    if (cleaned.startsWith('119')) return { status: 'valid', prefix: '119', color: '#16a34a', label: 'NIDA (119)' };
    if (cleaned.startsWith('120')) return { status: 'valid', prefix: '120', color: '#0369a1', label: 'NIDA (120)' };
    return { status: 'invalid', color: '#dc2626', label: 'Nimero itemewe' };
  };

  // ─── NEW: Get phone carrier hint ──────────────────────────────
  const getPhoneCarrier = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\s/g, '');
    if (cleaned.length < 4) return null;
    const prefix = cleaned.substring(0, 4);
    if (['0781', '0782', '0783', '0788', '0789'].some(p => prefix.startsWith(p.substring(0, Math.min(prefix.length, 4))))) {
      if (prefix.startsWith('078')) return { carrier: 'MTN', color: '#d97706', icon: 'bi-phone' };
    }
    if (prefix.startsWith('072') || prefix.startsWith('073')) return { carrier: 'Airtel', color: '#dc2626', icon: 'bi-phone' };
    return { carrier: 'Telefone', color: '#94a3b8', icon: 'bi-phone' };
  };

  const genderCount = (gender) =>
    records.filter((r) => r.igitsina === gender).length;

  // ─── Responsive CSS Keyframes & Global Styles ─────────────────────
  const globalCSS = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&display=swap');

    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    }

    html {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
      scroll-behavior: smooth;
    }

    body {
      
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    @keyframes gradientShift {
      0%, 100% { background-position: 0% center; }
      50% { background-position: 200% center; }
    }

    @keyframes slideInUp {
      from { opacity: 0; transform: translateY(30px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }

    @keyframes slideInDown {
      from { opacity: 0; transform: translateY(-20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fadeInScale {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.4); }
      70% { box-shadow: 0 0 0 10px rgba(22,163,74,0); }
      100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
    }

    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }

    @keyframes ripple {
      0% { transform: scale(0); opacity: 0.5; }
      100% { transform: scale(4); opacity: 0; }
    }

    @keyframes bounceIn {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.05); }
      70% { transform: scale(0.9); }
      100% { transform: scale(1); opacity: 1; }
    }

    @keyframes float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-6px); }
    }

    @keyframes shakeError {
      0%, 100% { transform: translateX(0); }
      10%, 50%, 90% { transform: translateX(-4px); }
      30%, 70% { transform: translateX(4px); }
    }

    @keyframes fadeInError {
      from { opacity: 0; transform: translateY(-6px); max-height: 0; }
      to { opacity: 1; transform: translateY(0); max-height: 60px; }
    }

    @keyframes validPulse {
      0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.3); }
      70% { box-shadow: 0 0 0 6px rgba(22,163,74,0); }
      100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
    }

    @keyframes statusBadgeIn {
      from { opacity: 0; transform: scale(0.7); }
      to { opacity: 1; transform: scale(1); }
    }

    .modern-input {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      touch-action: manipulation;
    }

    .modern-input:focus {
      border-color: #0B3D91 !important;
      box-shadow: 0 0 0 3px rgba(11,61,145,0.2), 0 0 24px rgba(11,61,145,0.12) !important;
      background: rgba(11,61,145,0.06) !important;
      outline: none !important;
    }

    .modern-input.input-error {
      border-color: #dc2626 !important;
      box-shadow: 0 0 0 3px rgba(220,38,38,0.15), 0 0 20px rgba(220,38,38,0.08) !important;
      animation: shakeError 0.4s ease;
    }

    .modern-input.input-error:focus {
      border-color: #dc2626 !important;
      box-shadow: 0 0 0 3px rgba(220,38,38,0.2), 0 0 24px rgba(220,38,38,0.12) !important;
    }

    .modern-input.input-valid {
      border-color: rgba(22,163,74,0.3) !important;
    }

    .modern-input.input-valid:focus {
      border-color: rgba(22,163,74,0.5) !important;
      box-shadow: 0 0 0 3px rgba(22,163,74,0.15), 0 0 24px rgba(22,163,74,0.08) !important;
    }

    .modern-input::placeholder {
      color: #64748b !important;
      opacity: 1;
    }

    .field-error-msg {
      animation: fadeInError 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      overflow: hidden;
    }

    .validation-badge {
      animation: statusBadgeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .modern-row {
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
    }

    .modern-row:hover {
      background: rgba(11,61,145,0.06) !important;
      transform: scale(1.002);
    }

    .modern-row:active {
      background: rgba(11,61,145,0.1) !important;
      transform: scale(0.998);
    }

    .submit-btn {
      position: relative;
      overflow: hidden;
      touch-action: manipulation;
    }

    .submit-btn::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      background: rgba(11,61,145,0.15);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: width 0.6s ease, height 0.6s ease;
    }

    .submit-btn:active::after {
      width: 300px;
      height: 300px;
    }

    .submit-btn:hover:not(:disabled) {
      transform: translateY(-3px);
      box-shadow: 0 14px 44px rgba(11,61,145,0.5) !important;
    }

    .submit-btn:active:not(:disabled) {
      transform: translateY(-1px);
    }

    .submit-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none !important;
    }

    .clear-btn {
      touch-action: manipulation;
      transition: all 0.3s ease;
    }

    .clear-btn:hover {
      border-color: rgba(11,61,145,0.25) !important;
      color: #94a3b8 !important;
      background: rgba(11,61,145,0.03) !important;
    }

    .clear-btn:active {
      transform: scale(0.97);
    }

    .card-animate {
      animation: slideInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .table-animate {
      animation: slideInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .stat-card {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      touch-action: manipulation;
      cursor: default;
    }

    .stat-card:hover {
      transform: translateY(-4px);
      border-color: rgba(11,61,145,0.3) !important;
      box-shadow: 0 12px 40px rgba(0,0,0,0.3);
    }

    .stat-card:active {
      transform: translateY(-2px) scale(0.98);
    }

    .person-card {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      touch-action: manipulation;
    }

    .person-card:hover {
      transform: translateY(-4px);
      border-color: rgba(11,61,145,0.3) !important;
      box-shadow: 0 16px 48px rgba(0,0,0,0.4) !important;
    }

    .person-card:active {
      transform: translateY(-2px) scale(0.98);
    }

    .toggle-btn {
      transition: all 0.3s ease;
      touch-action: manipulation;
    }

    .toggle-btn:hover {
      background: rgba(11,61,145,0.15) !important;
    }

    .toggle-btn:active {
      transform: scale(0.95);
    }

    .toggle-btn.active {
      background: linear-gradient(135deg, #0B3D91, #0369a1) !important;
      color: #ffffff !important;
      border-color: transparent !important;
    }

    .search-wrap input:focus {
      width: 100% !important;
      max-width: 400px !important;
    }

    .section-toggle {
      transition: all 0.3s ease;
      touch-action: manipulation;
    }

    .section-toggle:active {
      transform: scale(0.97);
    }

    /* Enhanced Date Input Styling */
    input[type="date"] {
      position: relative;
      cursor: pointer;
    }

    input[type="date"]::-webkit-calendar-picker-indicator {
      filter: invert(0.5) sepia(1) saturate(5) hue-rotate(240deg);
      cursor: pointer;
      padding: 6px;
      border-radius: 6px;
      transition: all 0.2s ease;
      opacity: 0.7;
    }

    input[type="date"]::-webkit-calendar-picker-indicator:hover {
      opacity: 1;
      background: rgba(11,61,145,0.1);
      filter: invert(0.3) sepia(1) saturate(8) hue-rotate(240deg);
    }

    input[type="date"]::-webkit-datetime-edit {
      color: #0d1b2a;
      padding: 0;
    }

    input[type="date"]::-webkit-datetime-edit-fields-wrapper {
      padding: 0;
    }

    input[type="date"]::-webkit-datetime-edit-day-field,
    input[type="date"]::-webkit-datetime-edit-month-field,
    input[type="date"]::-webkit-datetime-edit-year-field {
      color: #374151;
      padding: 2px 4px;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    input[type="date"]::-webkit-datetime-edit-day-field:focus,
    input[type="date"]::-webkit-datetime-edit-month-field:focus,
    input[type="date"]::-webkit-datetime-edit-year-field:focus {
      background: rgba(11,61,145,0.15);
      color: #0d1b2a;
      outline: none;
    }

    input[type="date"]::-webkit-datetime-edit-text {
      color: #64748b;
      padding: 0 3px;
    }

    /* Firefox date input */
    input[type="date"]::-moz-calendar-picker-indicator {
      filter: invert(1);
    }

    select option {
      background: #f0f4fb;
      color: #0d1b2a;
      padding: 8px;
    }

    /* Date display badge in table */
    .date-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 5px 10px;
      border-radius: 8px;
      background: rgba(11,61,145,0.06);
      border: 1px solid rgba(11,61,145,0.1);
      transition: all 0.2s ease;
      white-space: nowrap;
    }

    .date-badge:hover {
      background: rgba(11,61,145,0.1);
      border-color: rgba(11,61,145,0.2);
    }

    .nida-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 6px;
      font-size: 0.62rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      white-space: nowrap;
      flex-shrink: 0;
    }

    /* Table bottom accent line */
    .table-accent-bottom {
      height: 3px;
      background: linear-gradient(90deg, #0B3D91 0%, #0369a1 50%, transparent 100%);
      border-radius: 0 0 20px 20px;
    }

    /* Scrollbar styling */
    ::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    ::-webkit-scrollbar-track {
      background: rgba(11,61,145,0.02);
    }

    ::-webkit-scrollbar-thumb {
      background: rgba(11,61,145,0.3);
      border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: rgba(11,61,145,0.5);
    }

    /* iOS-specific fixes */
    @supports (-webkit-touch-callout: none) {
      .modern-input {
        font-size: 16px !important;
      }
    }

    /* Safe area for notched devices */
    @supports (padding: env(safe-area-inset-top)) {
      .page-container {
        padding-top: calc(16px + env(safe-area-inset-top));
        padding-bottom: calc(16px + env(safe-area-inset-bottom));
        padding-left: calc(16px + env(safe-area-inset-left));
        padding-right: calc(16px + env(safe-area-inset-right));
      }
    }

    /* Desktop hover media query */
    @media (hover: hover) and (pointer: fine) {
      .modern-row:hover {
        background: rgba(11,61,145,0.06) !important;
      }
    }

    /* Reduce motion for accessibility */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  // ─── Dynamic Styles ──────────────────────────────────────────────
  const s = {
    page: {
      minHeight: '100vh',
      minHeight: '100dvh',
      background: 'linear-gradient(160deg, #ffffff 0%, #ffffff 30%, #f0f4fb 60%, #ffffff 100%)',
      color: '#374151',
      fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      overflowX: 'hidden',
    },
    container: {
      padding: isMobile ? '20px 12px' : isTablet ? '32px 20px' : '48px 24px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(11,61,145,0.08)',
      border: '1px solid rgba(11,61,145,0.18)',
      borderRadius: '30px',
      padding: isMobile ? '6px 14px' : '8px 20px',
      fontSize: isMobile ? '0.7rem' : '0.78rem',
      color: '#0B3D91',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      backdropFilter: 'blur(10px)',
    },
    glowTitle: {
      background: 'linear-gradient(90deg, #0369a1, #0B3D91, #0B3D91, #0369a1)',
      backgroundSize: '300% auto',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      animation: 'gradientShift 4s ease infinite',
      letterSpacing: '-0.03em',
      fontSize: isMobile ? '1.6rem' : isTablet ? '2.2rem' : '2.8rem',
      fontWeight: '900',
      marginBottom: '8px',
      lineHeight: 1.1,
    },
    subtitle: {
      color: '#64748b',
      fontSize: isMobile ? '0.85rem' : '0.95rem',
      maxWidth: '500px',
      margin: '0 auto',
      lineHeight: 1.5,
    },
    statCard: {
      background: 'rgba(11,61,145,0.05)',
      border: '1px solid rgba(11,61,145,0.1)',
      borderRadius: isMobile ? '14px' : '18px',
      padding: isMobile ? '16px 12px' : '22px 24px',
      textAlign: 'center',
      backdropFilter: 'blur(10px)',
    },
    statIcon: {
      width: isMobile ? '32px' : '40px',
      height: isMobile ? '32px' : '40px',
      borderRadius: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '10px',
      fontSize: isMobile ? '0.9rem' : '1.1rem',
    },
    statNumber: {
      fontSize: isMobile ? '1.5rem' : '2rem',
      fontWeight: '900',
      background: 'linear-gradient(135deg, #0B3D91, #0369a1)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      lineHeight: 1,
    },
    statLabel: {
      fontSize: isMobile ? '0.65rem' : '0.72rem',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.12em',
      marginTop: '6px',
      fontWeight: '600',
    },
    card: {
      background: 'linear-gradient(160deg, #ffffff 0%, #f0f4fb 50%, #ffffff 100%)',
      border: '1px solid rgba(11,61,145,0.1)',
      borderRadius: isMobile ? '16px' : '24px',
      boxShadow: '0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(11,61,145,0.04)',
      overflow: 'hidden',
    },
    cardHeader: {
      background: 'linear-gradient(90deg, rgba(11,61,145,0.1), rgba(3,105,161,0.05), rgba(11,61,145,0.1))',
      borderBottom: '1px solid rgba(11,61,145,0.1)',
      padding: isMobile ? '16px 20px' : '20px 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    cardHeaderIcon: {
      width: isMobile ? '34px' : '42px',
      height: isMobile ? '34px' : '42px',
      borderRadius: isMobile ? '10px' : '13px',
      background: 'linear-gradient(135deg, #0B3D91, #0369a1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 6px 20px rgba(11,61,145,0.3)',
    },
    cardBody: {
      padding: isMobile ? '20px 16px' : isTablet ? '28px 24px' : '36px 32px',
    },
    inputLabel: {
      color: '#64748b',
      fontSize: isMobile ? '0.72rem' : '0.76rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    inputWrap: {
      position: 'relative',
    },
    inputField: {
      background: 'rgba(11,61,145,0.06)',
      border: '1.5px solid rgba(11,61,145,0.12)',
      borderRadius: isMobile ? '10px' : '13px',
      color: '#0d1b2a',
      padding: isMobile ? '14px 14px 14px 42px' : '16px 16px 16px 50px',
      fontSize: '16px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      width: '100%',
      outline: 'none',
      WebkitAppearance: 'none',
    },
    inputIcon: {
      position: 'absolute',
      left: isMobile ? '14px' : '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#0B3D91',
      fontSize: isMobile ? '1rem' : '1.1rem',
      zIndex: 4,
      pointerEvents: 'none',
      transition: 'color 0.3s ease',
    },
    inputIconActive: {
      color: '#0369a1',
    },
    submitBtn: {
      background: 'linear-gradient(135deg, #0B3D91 0%, #093278 40%, #0369a1 100%)',
      backgroundSize: '200% auto',
      border: 'none',
      borderRadius: isMobile ? '12px' : '14px',
      padding: isMobile ? '14px 28px' : '16px 48px',
      fontSize: isMobile ? '0.9rem' : '0.95rem',
      fontWeight: '800',
      color: '#ffffff',
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      boxShadow: '0 8px 32px rgba(11,61,145,0.35)',
      cursor: 'pointer',
      width: isMobile ? '100%' : 'auto',
      minHeight: '52px',
    },
    clearBtn: {
      background: 'transparent',
      border: '1.5px solid rgba(11,61,145,0.08)',
      borderRadius: isMobile ? '12px' : '14px',
      padding: isMobile ? '14px 24px' : '16px 32px',
      color: '#64748b',
      fontSize: isMobile ? '0.85rem' : '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      width: isMobile ? '100%' : 'auto',
      minHeight: '52px',
    },
    alert: (type) => ({
      background:
        type === 'success'
          ? 'rgba(22,163,74, 0.06)'
          : 'rgba(220,38,38, 0.06)',
      border: `1px solid ${
        type === 'success'
          ? 'rgba(22,163,74, 0.15)'
          : 'rgba(220,38,38, 0.15)'
      }`,
      borderRadius: isMobile ? '12px' : '14px',
      color: type === 'success' ? '#16a34a' : '#dc2626',
      padding: isMobile ? '14px 16px' : '16px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      animation: 'slideInDown 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      marginTop: '20px',
      fontSize: isMobile ? '0.85rem' : '0.9rem',
    }),
    tableWrap: {
      background: 'linear-gradient(160deg, #ffffff 0%, #f0f4fb 50%, #ffffff 100%)',
      border: '1px solid rgba(11,61,145,0.1)',
      borderRadius: isMobile ? '16px' : '20px',
      overflow: 'hidden',
      boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
      position: 'relative',
    },
    th: {
      color: '#0B3D91',
      fontWeight: '800',
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      padding: isMobile ? '14px 12px' : '16px 18px',
      border: 'none',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      background: 'rgba(240,244,251,0.97)',
      backdropFilter: 'blur(10px)',
      zIndex: 2,
      verticalAlign: 'middle',
    },
    td: {
      padding: isMobile ? '10px 12px' : '14px 18px',
      borderBottom: '1px solid rgba(11,61,145,0.06)',
      color: '#374151',
      fontSize: isMobile ? '0.82rem' : '0.88rem',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    personCard: {
      background: 'rgba(11,61,145,0.05)',
      border: '1px solid rgba(11,61,145,0.1)',
      borderRadius: '16px',
      padding: '18px',
      marginBottom: '12px',
      backdropFilter: 'blur(10px)',
    },
    avatar: {
      width: isMobile ? '34px' : '38px',
      height: isMobile ? '34px' : '38px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #0B3D91 0%, #0369a1 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ffffff',
      fontWeight: '800',
      fontSize: isMobile ? '0.72rem' : '0.8rem',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(11,61,145,0.3)',
    },
    genderBadge: (gender) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '700',
      background:
        gender === 'Gabo'
          ? 'rgba(3,105,161, 0.1)'
          : 'rgba(220,38,38, 0.1)',
      color: gender === 'Gabo' ? '#0369a1' : '#dc2626',
      border: `1px solid ${
        gender === 'Gabo'
          ? 'rgba(3,105,161,0.2)'
          : 'rgba(220,38,38,0.2)'
      }`,
    }),
    badge: {
      background: 'linear-gradient(135deg, #0B3D91, #0369a1)',
      color: '#ffffff',
      fontWeight: '800',
      fontSize: '0.78rem',
      padding: '5px 13px',
      borderRadius: '20px',
      display: 'inline-block',
    },
    pulseRing: {
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#16a34a',
      marginRight: '8px',
      boxShadow: '0 0 0 3px rgba(22,163,74,0.2)',
      animation: 'pulse 2s infinite',
    },
    searchInput: {
      background: 'rgba(11,61,145,0.06)',
      border: '1.5px solid rgba(11,61,145,0.12)',
      borderRadius: isMobile ? '10px' : '13px',
      color: '#0d1b2a',
      padding: isMobile ? '12px 14px 12px 40px' : '13px 16px 13px 46px',
      fontSize: '16px',
      transition: 'all 0.3s ease',
      width: '100%',
      maxWidth: isMobile ? '100%' : '340px',
      outline: 'none',
      WebkitAppearance: 'none',
    },
    emptyState: {
      padding: isMobile ? '48px 20px' : '72px 20px',
      textAlign: 'center',
    },
    footer: {
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderTop: '1px solid rgba(11,61,145,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: isMobile ? '0.72rem' : '0.78rem',
      color: '#64748b',
      flexWrap: 'wrap',
      gap: '8px',
      background: 'rgba(240,244,251,0.6)',
    },
    // ─── NEW: Validation styles ───────────────────────────────────
    fieldError: {
      color: '#dc2626',
      fontSize: isMobile ? '0.7rem' : '0.73rem',
      fontWeight: '600',
      marginTop: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      paddingLeft: '4px',
    },
    fieldSuccess: {
      position: 'absolute',
      right: isMobile ? '12px' : '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#16a34a',
      fontSize: '1rem',
      zIndex: 4,
      pointerEvents: 'none',
    },
    fieldErrorIcon: {
      position: 'absolute',
      right: isMobile ? '12px' : '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#dc2626',
      fontSize: '1rem',
      zIndex: 4,
      pointerEvents: 'none',
    },
    nidaStatusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 8px',
      borderRadius: '6px',
      fontSize: '0.62rem',
      fontWeight: '700',
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    datePreview: {
      marginTop: '8px',
      padding: '10px 14px',
      background: 'rgba(11,61,145,0.05)',
      border: '1px solid rgba(11,61,145,0.1)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '10px',
      flexWrap: 'wrap',
    },
    datePreviewText: {
      color: '#374151',
      fontSize: isMobile ? '0.82rem' : '0.88rem',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    ageBadgeInline: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '700',
      background: 'rgba(3,105,161,0.1)',
      color: '#0369a1',
      border: '1px solid rgba(3,105,161,0.2)',
    },
  };

  // ─── Field Config ────────────────────────────────────────────────
  const formFields = [
    {
      name: 'amazina_yombi',
      label: 'Amazina yombi',
      icon: 'bi-person-fill',
      type: 'text',
      placeholder: 'Andika amazina yombi...',
      required: true,
    },
    {
      name: 'igitsina',
      label: 'Igitsina',
      icon: 'bi-gender-ambiguous',
      type: 'select',
      options: [
        { value: 'Gabo', label: '♂  Gabo' },
        { value: 'Gore', label: '♀  Gore' },
      ],
      required: true,
    },
    {
      name: 'itariki_amavuko',
      label: "Itariki y'amavuko",
      icon: 'bi-calendar3',
      type: 'date',
    },
    {
      name: 'nimero_nida',
      label: 'Nimero ya NIDA',
      icon: 'bi-credit-card-2-front-fill',
      type: 'text',
      placeholder: '1 1990 8 0000000 0 00',
      maxLength: 22, // increased for formatted spaces
      mono: true,
    },
    {
      name: 'nimero_telefone',
      label: 'Nimero ya telefone',
      icon: 'bi-telephone-fill',
      type: 'tel',
      placeholder: '078X XXX XXX',
    },
    {
      name: 'nimero_wihutirwa',
      label: "Numero y'ihutirwa",
      icon: 'bi-telephone-outbound-fill',
      type: 'tel',
      placeholder: '078X XXX XXX',
    },
  ];

  // ─── Render Input ────────────────────────────────────────────────
  const renderField = (field) => {
    const isActive = activeField === field.name;
    const hasError = touchedFields[field.name] && fieldErrors[field.name];
    const isTouched = touchedFields[field.name];
    const isValid = isTouched && !fieldErrors[field.name] && formData[field.name];

    const iconStyle = {
      ...s.inputIcon,
      ...(isActive ? s.inputIconActive : {}),
      ...(hasError ? { color: '#dc2626' } : {}),
      ...(isValid ? { color: '#16a34a' } : {}),
    };

    const inputStyle = {
      ...s.inputField,
      ...(field.mono
        ? {
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            letterSpacing: '0.06em',
          }
        : {}),
    };

    // Determine validation class
    const validationClass = hasError ? 'input-error' : isValid ? 'input-valid' : '';

    // NIDA status
    const nidaStatus = field.name === 'nimero_nida' ? getNidaStatus(formData.nimero_nida) : null;

    // Phone carrier
    const phoneCarrier = (field.name === 'nimero_telefone' || field.name === 'nimero_wihutirwa')
      ? getPhoneCarrier(formData[field.name])
      : null;

    // Date age
    const dateAge = field.name === 'itariki_amavuko' && formData.itariki_amavuko
      ? calculateAge(formData.itariki_amavuko)
      : null;

    return (
      <div className={isMobile ? 'col-12' : 'col-md-6'} key={field.name}>
        <label style={s.inputLabel}>
          <i className={`bi ${field.icon}`} style={{ fontSize: '0.8rem' }}></i>
          {field.label}
          {field.required && (
            <span style={{ color: '#dc2626', marginLeft: '2px' }}>*</span>
          )}
          {/* NIDA Status Badge in label */}
          {nidaStatus && nidaStatus.status === 'valid' && (
            <span
              className="validation-badge"
              style={{
                ...s.nidaStatusBadge,
                background: `${nidaStatus.color}15`,
                color: nidaStatus.color,
                border: `1px solid ${nidaStatus.color}30`,
                marginLeft: 'auto',
              }}
            >
              <i className="bi bi-shield-check" style={{ fontSize: '0.6rem' }}></i>
              {nidaStatus.label}
            </span>
          )}
          {/* Phone Carrier Badge in label */}
          {phoneCarrier && formData[field.name].replace(/\s/g, '').length >= 4 && (
            <span
              className="validation-badge"
              style={{
                ...s.nidaStatusBadge,
                background: `${phoneCarrier.color}15`,
                color: phoneCarrier.color,
                border: `1px solid ${phoneCarrier.color}30`,
                marginLeft: 'auto',
              }}
            >
              <i className={`bi ${phoneCarrier.icon}`} style={{ fontSize: '0.6rem' }}></i>
              {phoneCarrier.carrier}
            </span>
          )}
        </label>
        <div style={s.inputWrap}>
          <i className={`bi ${field.icon}`} style={iconStyle}></i>

          {/* Validation status icon on right */}
          {isTouched && formData[field.name] && field.type !== 'select' && (
            hasError ? (
              <i className="bi bi-exclamation-circle-fill" style={s.fieldErrorIcon}></i>
            ) : isValid ? (
              <i className="bi bi-check-circle-fill" style={s.fieldSuccess}></i>
            ) : null
          )}

          {field.type === 'select' ? (
            <select
              className="modern-input"
              style={{
                ...inputStyle,
                cursor: 'pointer',
              }}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              onFocus={() => setActiveField(field.name)}
              onBlur={() => handleBlur(field.name)}
              required={field.required}
            >
              {field.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : field.type === 'date' ? (
            <DateOfBirthInput
              className={`modern-input ${validationClass}`}
              style={inputStyle}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              onFocus={() => setActiveField(field.name)}
              onBlur={() => handleBlur(field.name)}
              required={field.required}
            />
          ) : (
            <input
              type={field.type}
              className={`modern-input ${validationClass}`}
              style={inputStyle}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              onFocus={() => setActiveField(field.name)}
              onBlur={() => handleBlur(field.name)}
              placeholder={field.placeholder || ''}
              required={field.required}
              maxLength={field.maxLength}
              autoComplete="off"
            />
          )}
        </div>

        {/* ─── Field Error Message ──────────────────────────────── */}
        {hasError && (
          <div className="field-error-msg" style={s.fieldError}>
            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '0.7rem' }}></i>
            {fieldErrors[field.name]}
          </div>
        )}

        {/* ─── NIDA Invalid Prefix Warning ─────────────────────── */}
        {field.name === 'nimero_nida' && nidaStatus && nidaStatus.status === 'invalid' && !hasError && (
          <div className="field-error-msg" style={s.fieldError}>
            <i className="bi bi-shield-exclamation" style={{ fontSize: '0.7rem' }}></i>
            Nimero ya NIDA igomba gutangira na <strong style={{ color: '#16a34a', margin: '0 4px' }}>119</strong> cyangwa <strong style={{ color: '#0369a1', margin: '0 4px' }}>120</strong>
          </div>
        )}

        {/* ─── Date Preview with Age ───────────────────────────── */}
        {field.name === 'itariki_amavuko' && formData.itariki_amavuko && !hasError && (
          <div style={s.datePreview} className="validation-badge">
            <span style={s.datePreviewText}>
              <i className="bi bi-calendar-heart" style={{ color: '#0B3D91', fontSize: '0.9rem' }}></i>
              {formatDateDisplay(formData.itariki_amavuko)}
            </span>
            {dateAge !== null && dateAge >= 0 && (
              <span style={s.ageBadgeInline}>
                <i className="bi bi-hourglass-split" style={{ fontSize: '0.7rem' }}></i>
                {dateAge} {dateAge === 1 ? 'umwaka' : 'imyaka'}
              </span>
            )}
          </div>
        )}

        {/* ─── NIDA Format Helper ──────────────────────────────── */}
        {field.name === 'nimero_nida' && isActive && !formData.nimero_nida && (
          <div style={{
            marginTop: '6px',
            padding: '8px 12px',
            background: 'rgba(11,61,145,0.04)',
            border: '1px solid rgba(11,61,145,0.08)',
            borderRadius: '8px',
            fontSize: '0.72rem',
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }} className="validation-badge">
            <i className="bi bi-info-circle" style={{ color: '#0B3D91', fontSize: '0.7rem' }}></i>
            Tangira na <strong style={{ color: '#16a34a' }}>119</strong> cyangwa <strong style={{ color: '#0369a1' }}>120</strong> — Imibare 16
          </div>
        )}
      </div>
    );
  };

  // ─── Render Person Card (Mobile) ─────────────────────────────────
  const renderPersonCard = (person, idx) => {
    const nidaStatus = getNidaStatus(person.nimero_nida);

    return (
      <div
        key={person.id}
        className="person-card"
        style={{
          ...s.personCard,
          animation: `fadeInScale 0.4s cubic-bezier(0.16,1,0.3,1) ${idx * 0.06}s both`,
        }}
      >
        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '10px' }}>
          <button
            onClick={() => handleEdit(person)}
            style={{
              background: 'rgba(11,61,145,0.12)',
              border: '1px solid rgba(11,61,145,0.25)',
              borderRadius: '8px',
              color: '#0B3D91',
              padding: '5px 12px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <i className="bi bi-pencil-fill"></i> Hindura
          </button>
          <button
            onClick={() => setDeleteConfirmId(person.id)}
            style={{
              background: 'rgba(220,38,38,0.1)',
              border: '1px solid rgba(220,38,38,0.2)',
              borderRadius: '8px',
              color: '#dc2626',
              padding: '5px 12px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <i className="bi bi-trash-fill"></i> Siba
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '14px',
          }}
        >
          <div style={s.avatar}>{getInitials(person.amazina_yombi)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: '700',
                color: '#0d1b2a',
                fontSize: '0.95rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {person.amazina_yombi}
            </div>
            <span style={s.genderBadge(person.igitsina)}>
              <i
                className={`bi ${
                  person.igitsina === 'Gabo'
                    ? 'bi-gender-male'
                    : 'bi-gender-female'
                }`}
              ></i>
              {person.igitsina}
            </span>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            fontSize: '0.8rem',
          }}
        >
          {[
            {
              icon: 'bi-calendar-heart',
              label: 'Amavuko',
              value: formatDateDisplay(person.itariki_amavuko),
            },
            {
              icon: 'bi-hourglass-split',
              label: 'Imyaka',
              value: calculateAge(person.itariki_amavuko),
            },
            {
              icon: 'bi-credit-card',
              label: 'NIDA',
              value: person.nimero_nida,
              extra: nidaStatus?.status === 'valid' ? nidaStatus : null,
            },
            {
              icon: 'bi-telephone',
              label: 'Telefone',
              value: person.nimero_telefone,
            },
            {
              icon: 'bi-clock',
              label: 'Iyandikishwa',
              value: person.created_at
                ? new Date(person.created_at).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : null,
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                background: 'rgba(11,61,145,0.06)',
                borderRadius: '10px',
                padding: '10px 12px',
                border: '1px solid rgba(11,61,145,0.1)'
              }}
            >
              <div
                style={{
                  color: '#64748b',
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <i className={`bi ${item.icon}`}></i>
                {item.label}
                {/* NIDA prefix badge */}
                {item.extra && (
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '0.58rem',
                    color: item.extra.color,
                    fontWeight: '800',
                  }}>
                    {item.extra.prefix}
                  </span>
                )}
              </div>
              <div
                style={{
                  color: item.value !== null && item.value !== undefined ? '#374151' : '#64748b',
                  fontWeight: '600',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.value !== null && item.value !== undefined ? item.value : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{globalCSS}</style>

      {/* ─── Delete Confirmation Modal ──────────────────────── */}
      {deleteConfirmId && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => setDeleteConfirmId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(160deg, #ffffff 0%, #f0f4fb 100%)',
              border: '1px solid rgba(220,38,38,0.2)',
              borderRadius: '20px',
              padding: '32px 28px',
              maxWidth: '380px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
              animation: 'bounceIn 0.3s ease',
              textAlign: 'center',
            }}
          >
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '16px',
              background: 'rgba(220,38,38,0.1)',
              border: '1px solid rgba(220,38,38,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <i className="bi bi-trash-fill" style={{ color: '#dc2626', fontSize: '1.4rem' }}></i>
            </div>
            <h3 style={{ color: '#0d1b2a', fontWeight: '800', marginBottom: '8px', fontSize: '1.1rem' }}>
              Siba Umuntu?
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.88rem', marginBottom: '24px' }}>
              Ibi ntishobora gusubizwa inyuma. Uremeza gusiba uyu muntu?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteConfirmId(null)}
                style={{
                  background: 'rgba(11,61,145,0.05)',
                  border: '1px solid rgba(11,61,145,0.1)',
                  borderRadius: '12px',
                  color: '#94a3b8',
                  padding: '12px 24px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                Reka
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleting}
                style={{
                  background: 'linear-gradient(135deg, #dc2626, #dc2626)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  padding: '12px 24px',
                  fontWeight: '800',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  opacity: deleting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {deleting ? (
                  <><span className="spinner-border spinner-border-sm"></span> Tegereza...</>
                ) : (
                  <><i className="bi bi-trash-fill"></i> Yego, Siba</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={s.page}>
        <div className="page-container" style={s.container}>
          {/* ─── Title Section ──────────────────────────────────── */}
          <div
            className="text-center"
            style={{
              marginBottom: isMobile ? '28px' : '44px',
              animation: 'slideInDown 0.5s ease',
            }}
          >
            <div style={{ marginBottom: '14px' }}>
              <span style={s.titleBadge}>
                <i className="bi bi-shield-lock-fill"></i>
                Sisitemu y'Umwirondoro
              </span>
            </div>
            <h1 style={s.glowTitle}>Kwiyandikisha kw'Umuntu</h1>
            <p style={s.subtitle}>
              Andika amakuru y'umuntu mushya mu buryo bworoshye kandi bwizewe
            </p>
          </div>

          {/* ─── Stats Row ──────────────────────────────────────── */}
          <div
            className="row g-3"
            style={{ marginBottom: isMobile ? '24px' : '40px' }}
          >
            {[
              {
                icon: 'bi-people-fill',
                color: '#0B3D91',
                bg: 'rgba(11,61,145,0.1)',
                count: records.length,
                label: 'Abantu Bose',
              },
              {
                icon: 'bi-gender-male',
                color: '#0369a1',
                bg: 'rgba(3,105,161,0.1)',
                count: genderCount('Gabo'),
                label: 'Abagabo',
              },
              {
                icon: 'bi-gender-female',
                color: '#dc2626',
                bg: 'rgba(220,38,38,0.1)',
                count: genderCount('Gore'),
                label: 'Abagore',
              },
            ].map((stat, i) => (
              <div className="col-4" key={i}>
                <div
                  className="stat-card"
                  style={{
                    ...s.statCard,
                    animation: `bounceIn 0.5s cubic-bezier(0.16,1,0.3,1) ${
                      i * 0.1
                    }s both`,
                  }}
                >
                  <div
                    style={{
                      ...s.statIcon,
                      background: stat.bg,
                      color: stat.color,
                    }}
                  >
                    <i className={`bi ${stat.icon}`}></i>
                  </div>
                  <div style={s.statNumber}>{stat.count}</div>
                  <div style={s.statLabel}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ─── Form Card ──────────────────────────────────────── */}
          <div style={s.card} className="card-animate" >
            <div
              style={s.cardHeader}
              className="section-toggle"
              onClick={() => setShowForm(!showForm)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') &&
                setShowForm(!showForm)
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div style={s.cardHeaderIcon}>
                  <i
                    className={`bi ${editingId ? 'bi-pencil-fill' : 'bi-person-plus-fill'}`}
                    style={{ color: '#ffffff', fontSize: isMobile ? '0.95rem' : '1.1rem' }}
                  ></i>
                </div>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: isMobile ? '1rem' : '1.12rem',
                      fontWeight: '800',
                      color: '#0d1b2a',
                    }}
                  >
                    {editingId ? 'Hindura Amakuru' : "Fomu y'Iyandikisha"}
                  </h2>
                  <p
                    style={{
                      margin: 0,
                      fontSize: isMobile ? '0.72rem' : '0.78rem',
                      color: '#64748b',
                    }}
                  >
                    Uzuza amakuru yose akenewe
                  </p>
                </div>
              </div>
              <i
                className={`bi bi-chevron-${showForm ? 'up' : 'down'}`}
                style={{
                  color: '#64748b',
                  fontSize: '1.2rem',
                  transition: 'transform 0.3s ease',
                }}
              ></i>
            </div>

            <div
              style={{
                ...s.cardBody,
                maxHeight: showForm ? '2000px' : '0',
                overflow: 'hidden',
                padding: showForm ? s.cardBody.padding : '0 16px',
                opacity: showForm ? 1 : 0,
                transition:
                  'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <form onSubmit={handleSubmit}>
                <div className="row g-3 g-md-4">
                  {formFields.map(renderField)}
                </div>

                {/* ─── Validation Summary ──────────────────────── */}
                {Object.keys(fieldErrors).length > 0 && Object.keys(touchedFields).length > 2 && (
                  <div style={{
                    marginTop: '20px',
                    padding: isMobile ? '12px 14px' : '14px 20px',
                    background: 'rgba(220,38,38,0.04)',
                    border: '1px solid rgba(220,38,38,0.1)',
                    borderRadius: '12px',
                    animation: 'fadeInScale 0.3s ease',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      fontSize: '0.78rem',
                      fontWeight: '700',
                      color: '#dc2626',
                    }}>
                      <i className="bi bi-shield-exclamation"></i>
                      Amakosa {Object.keys(fieldErrors).length} yo gukosora
                    </div>
                    {Object.entries(fieldErrors).map(([key, msg]) => (
                      <div key={key} style={{
                        fontSize: '0.72rem',
                        color: '#dc2626',
                        padding: '2px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        <i className="bi bi-dot" style={{ fontSize: '0.9rem' }}></i>
                        {msg}
                      </div>
                    ))}
                  </div>
                )}

                {/* Buttons */}
                <div
                  style={{
                    marginTop: isMobile ? '24px' : '32px',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? '12px' : '16px',
                  }}
                >
                  <button
                    type="submit"
                    className="submit-btn"
                    style={s.submitBtn}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Tegereza...
                      </>
                    ) : (
                      <>
                        <i className={`bi ${editingId ? 'bi-pencil-fill' : 'bi-check-circle-fill'} me-2`}></i>
                        {editingId ? 'Vugurura Amakuru' : 'Andika Umuntu'}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="clear-btn"
                    style={s.clearBtn}
                    onClick={resetForm}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Siba
                  </button>
                </div>

                {/* Alerts */}
                {success && (
                  <div style={s.alert('success')}>
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ fontSize: '1.2rem', flexShrink: 0 }}
                    ></i>
                    <span style={{ fontWeight: '600' }}>{success}</span>
                  </div>
                )}
                {error && (
                  <div style={s.alert('error')}>
                    <i
                      className="bi bi-exclamation-triangle-fill"
                      style={{ fontSize: '1.2rem', flexShrink: 0 }}
                    ></i>
                    <span style={{ fontWeight: '600' }}>{error}</span>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* ─── Spacer ──────────────────────────────────────────── */}
          <div style={{ height: isMobile ? '28px' : '44px' }}></div>

          {/* ─── Table / Cards Section ───────────────────────────── */}
          <div className="table-animate">
            {/* Section Header */}
            <div
              style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div style={s.cardHeaderIcon}>
                  <i
                    className="bi bi-table"
                    style={{ color: '#ffffff', fontSize: '1rem' }}
                  ></i>
                </div>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: isMobile ? '1rem' : '1.12rem',
                      fontWeight: '800',
                      color: '#0d1b2a',
                    }}
                  >
                    Urutonde rw'Abantu
                  </h2>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '4px',
                    }}
                  >
                    <span style={s.pulseRing}></span>
                    <span style={s.badge}>{filteredRecords.length}</span>
                    <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                      banditse
                    </span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: isMobile ? '100%' : 'auto',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                {/* View Toggle */}
                {!isMobile && (
                  <div
                    style={{
                      display: 'flex',
                      background: 'rgba(11,61,145,0.03)',
                      borderRadius: '10px',
                      border: '1px solid rgba(11,61,145,0.1)',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      className={`toggle-btn ${
                        viewMode === 'table' ? 'active' : ''
                      }`}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '8px 14px',
                        color: '#64748b',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                      onClick={() => setViewMode('table')}
                    >
                      <i className="bi bi-list"></i>
                    </button>
                    <button
                      className={`toggle-btn ${
                        viewMode === 'cards' ? 'active' : ''
                      }`}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: '8px 14px',
                        color: '#64748b',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                      onClick={() => setViewMode('cards')}
                    >
                      <i className="bi bi-grid-3x2-gap"></i>
                    </button>
                  </div>
                )}

                {/* Search */}
                <div
                  className="search-wrap"
                  style={{
                    position: 'relative',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  <i
                    className="bi bi-search"
                    style={{
                      position: 'absolute',
                      left: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#64748b',
                      fontSize: '0.88rem',
                      pointerEvents: 'none',
                    }}
                  ></i>
                  <input
                    type="text"
                    className="modern-input"
                    style={s.searchInput}
                    placeholder="Shakisha umuntu..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(11,61,145,0.05)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b',
                        cursor: 'pointer',
                        fontSize: '0.7rem',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div
                style={{
                  ...s.tableWrap,
                  padding: isMobile ? '60px 20px' : '80px 20px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '3px solid rgba(11,61,145,0.15)',
                    borderTopColor: '#0B3D91',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    margin: '0 auto 20px',
                  }}
                ></div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <p
                  style={{
                    color: '#64748b',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                  }}
                >
                  Gutegereza amakuru...
                </p>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div style={s.tableWrap}>
                <div style={s.emptyState}>
                  <div
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '20px',
                      background: 'rgba(11,61,145,0.03)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                      border: '1px solid rgba(11,61,145,0.1)',
                    }}
                  >
                    <i
                      className={`bi ${
                        searchTerm ? 'bi-search' : 'bi-inbox'
                      }`}
                      style={{
                        fontSize: '2rem',
                        color: '#64748b',
                      }}
                    ></i>
                  </div>
                  <h4
                    style={{
                      color: '#64748b',
                      fontWeight: '800',
                      marginBottom: '8px',
                      fontSize: isMobile ? '1rem' : '1.15rem',
                    }}
                  >
                    {searchTerm
                      ? 'Nta mubazo ubonetse'
                      : 'Nta muntu wanditse'}
                  </h4>
                  <p
                    style={{
                      color: '#64748b',
                      fontSize: '0.88rem',
                      maxWidth: '340px',
                      margin: '0 auto',
                    }}
                  >
                    {searchTerm
                      ? 'Gerageza izindi mvugo zo gushakisha.'
                      : 'Koresha fomu iri hejuru wandike umuntu mushya.'}
                  </p>
                </div>
              </div>
            ) : viewMode === 'cards' || isMobile ? (
              <div
                className="row g-3"
                style={{
                  ...(isDesktop ? {} : {}),
                }}
              >
                {filteredRecords.map((person, idx) => (
                  <div
                    className={isMobile ? 'col-12' : isTablet ? 'col-6' : 'col-6 col-lg-4'}
                    key={person.id}
                  >
                    {renderPersonCard(person, idx)}
                  </div>
                ))}
              </div>
            ) : (
              <div style={s.tableWrap}>
                <div
                  style={{
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={s.th}>Umuntu</th>
                        <th style={s.th}>Igitsina</th>
                        <th style={s.th}>Itariki y'amavuko</th>
                        <th style={s.th}>Imyaka</th>
                        <th style={s.th}>NIDA</th>
                        <th style={s.th}>Telefone</th>
                        <th style={s.th}>Iyandikishwa</th>
                        <th style={s.th}>Ibikorwa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map((person, idx) => {
                        const personNidaStatus = getNidaStatus(person.nimero_nida);

                        return (
                          <tr
                            key={person.id}
                            className="modern-row"
                            style={{
                              animation: `fadeInScale 0.35s cubic-bezier(0.16,1,0.3,1) ${
                                idx * 0.04
                              }s both`,
                            }}
                          >
                            <td style={s.td}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                }}
                              >
                                <div style={s.avatar}>
                                  {getInitials(person.amazina_yombi)}
                                </div>
                                <span
                                  style={{
                                    fontWeight: '700',
                                    color: '#0d1b2a',
                                  }}
                                >
                                  {person.amazina_yombi}
                                </span>
                              </div>
                            </td>
                            <td style={s.td}>
                              <span style={s.genderBadge(person.igitsina)}>
                                <i
                                  className={`bi ${
                                    person.igitsina === 'Gabo'
                                      ? 'bi-gender-male'
                                      : 'bi-gender-female'
                                  }`}
                                ></i>
                                {person.igitsina}
                              </span>
                            </td>
                            {/* ─── Styled Date Cell ──────────────── */}
                            <td style={s.td}>
                              {person.itariki_amavuko ? (
                                <div className="date-badge">
                                  <i
                                    className="bi bi-calendar-heart"
                                    style={{
                                      color: '#0B3D91',
                                      fontSize: '0.85rem',
                                    }}
                                  ></i>
                                  <div>
                                    <div style={{ fontWeight: '600', color: '#374151', fontSize: '0.84rem' }}>
                                      {formatDateDisplay(person.itariki_amavuko)}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            {/* ─── Age Cell ──────────────────────── */}
                            <td style={s.td}>
                              {calculateAge(person.itariki_amavuko) !== null ? (
                                <span style={s.ageBadgeInline}>
                                  <i className="bi bi-hourglass-split" style={{ fontSize: '0.7rem' }}></i>
                                  {calculateAge(person.itariki_amavuko)}
                                </span>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            {/* ─── NIDA Cell with prefix badge ──── */}
                            <td style={s.td}>
                              {person.nimero_nida ? (
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                                  <span
                                    style={{
                                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                      fontSize: '0.82rem',
                                      color: '#64748b',
                                      letterSpacing: '0.04em',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {person.nimero_nida}
                                  </span>
                                  {personNidaStatus?.status === 'valid' && (
                                    <span style={{
                                      ...s.nidaStatusBadge,
                                      background: `${personNidaStatus.color}12`,
                                      color: personNidaStatus.color,
                                      border: `1px solid ${personNidaStatus.color}25`,
                                    }}>
                                      <i className="bi bi-shield-check" style={{ fontSize: '0.55rem' }}></i>
                                      {personNidaStatus.label}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            <td style={s.td}>
                              {person.nimero_telefone ? (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    color: '#16a34a',
                                    fontWeight: '600',
                                  }}
                                >
                                  <i
                                    className="bi bi-telephone-fill"
                                    style={{ fontSize: '0.78rem', flexShrink: 0 }}
                                  ></i>
                                  {person.nimero_telefone}
                                </span>
                              ) : (
                                <span style={{ color: '#64748b' }}>—</span>
                              )}
                            </td>
                            <td style={s.td}>
                              <span
                                style={{
                                  color: '#64748b',
                                  fontSize: '0.82rem',
                                }}
                              >
                                {person.created_at
                                  ? new Date(
                                      person.created_at
                                    ).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </span>
                            </td>
                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'inline-flex', flexDirection: 'row', gap: '6px', flexWrap: 'nowrap', alignItems: 'center' }}>
                                <button
                                  onClick={() => handleEdit(person)}
                                  title="Hindura"
                                  style={{
                                    background: 'rgba(11,61,145,0.1)',
                                    border: '1px solid rgba(11,61,145,0.2)',
                                    borderRadius: '8px',
                                    color: '#0B3D91',
                                    padding: '5px 11px',
                                    fontSize: '0.78rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    fontWeight: '700',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s',
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className="bi bi-pencil-fill" style={{ fontSize: '0.75rem' }}></i>
                                  Hindura
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmId(person.id)}
                                  title="Siba"
                                  style={{
                                    background: 'rgba(220,38,38,0.08)',
                                    border: '1px solid rgba(220,38,38,0.18)',
                                    borderRadius: '8px',
                                    color: '#dc2626',
                                    padding: '5px 11px',
                                    fontSize: '0.78rem',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    fontWeight: '700',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s',
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className="bi bi-trash-fill" style={{ fontSize: '0.75rem' }}></i>
                                  Siba
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Table Footer */}
                <div style={s.footer}>
                  <span>
                    Igaragaza{' '}
                    <strong style={{ color: '#64748b' }}>
                      {filteredRecords.length}
                    </strong>{' '}
                    muri{' '}
                    <strong style={{ color: '#64748b' }}>
                      {records.length}
                    </strong>{' '}
                    byose
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <i className="bi bi-clock"></i>
                    {new Date().toLocaleTimeString()}
                  </span>
                </div>
                <div className="table-accent-bottom"></div>
              </div>
            )}

            {/* Cards Footer */}
            {(viewMode === 'cards' || isMobile) &&
              filteredRecords.length > 0 && (
                <div
                  style={{
                    ...s.footer,
                    background: 'transparent',
                    borderTop: 'none',
                    marginTop: '16px',
                    justifyContent: 'center',
                  }}
                >
                  <span>
                    Igaragaza{' '}
                    <strong style={{ color: '#64748b' }}>
                      {filteredRecords.length}
                    </strong>{' '}
                    muri{' '}
                    <strong style={{ color: '#64748b' }}>
                      {records.length}
                    </strong>{' '}
                    byose
                  </span>
                </div>
              )}
          </div>

          {/* ─── Bottom Safe Area Spacer ─────────────────────────── */}
          <div
            style={{
              height: isMobile ? '40px' : '20px',
            }}
          ></div>
        </div>
      </div>
    </>
  );
};

export default UmuntuUmwirondoro;